<template>
  <div data-view="register" v-if="!isExisting">
    <div class="step-counter">
      {{ 'Step ' + step + ' of ' + steps }}
    </div>
    <UserProfileForm
      v-if="!isSecondStepActive"
      :user-profile="userProfile"
      :on-submit="onGetUserProfile"
    />
    <BusinessProfileForm
      v-else
      :business-profile="businessProfile"
      :is-loading="isLoading"
      :is-submitted="isSubmitted"
      :has-recaptcha="isRecaptchaEnabled"
      cancel-trigger-label="Back"
      :on-cancel="onDisplayUserProfile"
      :on-submit="onGetBusinessProfile"
    />
  </div>
</template>

<script>
  import UserProfileForm from '../../components/forms/auth/UserProfile';
  import BusinessProfileForm from '../../components/forms/auth/BusinessProfile';

  export default {
    components: {
      UserProfileForm,
      BusinessProfileForm,
    },
    data() {
      return {
        isRecaptchaEnabled: import.meta.env.VUE_APP_USE_RECAPTCHA === 'true',
        stepBoxPos: {
          top: 58,
          right: 0,
        },
        userProfile: {
          firstName: '',
          lastName: '',
          title: '',
          email: '',
          password: '',
          language: 'en',
          timezone: 1,
        },
        businessProfile: {
          name: '',
          shopUrl: '',
          invoiceEmail: '',
          address: '',
          secondAddress: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'de',
        },
        step: 1,
        steps: 2,
        isTypeDetermined: false,
        isExisting: false,
        isSecondStepActive: false,
        isLoading: false,
        isSubmitted: false,
      };
    },
    created() {
      // $watch needs a callback; passing only a source fn made Vue read
      // `callback.handler` off undefined and throw on every mount. Watch the user
      // and error-count sources and run the side effects in the callback
      // (immediate keeps the original run-on-created behavior).
      this.$watch(
        () => [
          this.$store.getters['auth/user'],
          this.$store.getters['alerts/error'].length,
        ],
        () => {
          const user = this.$store.getters['auth/user'];
          const hasErrors = this.$store.getters['alerts/error'].length > 0;

          if (user) {
            this.watchUser(user);
          }

          if (hasErrors && this.isSubmitted) {
            this.isLoading = false;
            this.isSubmitted = false;
          }
        },
        { immediate: true },
      );
    },
    methods: {
      onGetUserProfile(userProfile) {
        this.userProfile = userProfile;
        this.isSecondStepActive = true;
        this.step = 2;
      },
      onGetBusinessProfile(businessProfile) {
        this.businessProfile = businessProfile;
        this.createAccount();
      },
      onDisplayUserProfile() {
        this.isSecondStepActive = false;
        this.step = 1;
      },
      createAccount() {
        const { token } = this.$route.params;
        const {
          firstName,
          lastName,
          title,
          email,
          password,
          language,
          timezone,
          enableCommunication,
        } = this.userProfile;
        const {
          name,
          shopUrl,
          invoiceEmail,
          address,
          secondAddress,
          city,
          state,
          zipCode,
          country,
          recaptcha,
        } = this.businessProfile;

        const user = {
          firstname: firstName,
          lastname: lastName,
          email,
          token,
          password,
          employee: title,
          address,
          address_opt: secondAddress,
          city,
          state,
          zip: zipCode,
          country,
          invoice_email: invoiceEmail,
          name,
          url: shopUrl,
          language,
          timezone,
          'g-recaptcha-response': recaptcha,
          terms_accepted:
            this.userProfile.acceptedTerms || this.businessProfile.acceptedTerms,
          enabled_communication: enableCommunication,
        };

        this.isLoading = true;
        this.isSubmitted = true;
        return this.$store.dispatch('auth/register', user).catch(() => {
          this.isLoading = false;
          this.isSubmitted = false;
        });
      },
      watchUser(user) {
        const { firstname, lastname, email, language } = user;
        const { firstName } = this.userProfile;

        if (firstname !== firstName) {
          this.userProfile = {
            ...this.userProfile,
            firstName: firstname,
            lastName: lastname,
            email,
            language,
          };
        }
      },
    },
    metaInfo() {
      return {
        title: this.$t('views_register_title'),
      };
    },
  };
</script>

<style lang="scss" scoped>
@use '../../assets/styles/theme' as *;

[data-view='register'] {
  width: 40%;
  position: relative;

  .step-counter {
    position: absolute;
    top: 104px;
    right: 5.4vw;
    font-size: $text-size-md;
  }

  @include below(md) {
    display: flex;
    flex-direction: column;
    width: 100%;

    .step-counter {
      position: static;
      padding: 24px 16px 0;
      text-align: right;
    }
  }
}
</style>
